import React, { useMemo } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';

const PALETTES = {
  default: ['#008080', '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#16a34a'],
  warm: ['#dc2626', '#ea580c', '#d97706', '#ca8a04', '#e11d48'],
  cool: ['#0284c7', '#0891b2', '#0d9488', '#2563eb', '#7c3aed'],
  vibrant: ['#7c3aed', '#db2777', '#ea580c', '#16a34a', '#2563eb']
};

const SheetChart = ({ result, primaryColor = '#008080', onSelect, selectedValue = '' }) => {
  const palette = useMemo(() => {
    const configured = PALETTES[result?.visual?.colorScheme] || PALETTES.default;
    return result?.visual?.colorScheme === 'workspace' || result?.visual?.colorScheme === 'sheet'
      ? [primaryColor, ...configured.filter((color) => color !== primaryColor)]
      : configured;
  }, [primaryColor, result?.visual?.colorScheme]);
  const series = Array.isArray(result?.series) && result.series.length ? result.series : ['Value'];
  const data = (Array.isArray(result?.data) ? result.data : []).map((item) => ({
    name: item.name,
    ...(item.values || {})
  }));
  const selectValue = (value) => onSelect?.(`${value ?? ''}`);
  const selectionControl = onSelect ? (
    <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-slate-500">
      <span className="shrink-0">Filter by</span>
      <select value={selectedValue} onChange={(event) => selectValue(event.target.value)} className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-[#008080]">
        <option value="">All values</option>
        {data.map((item) => <option key={item.name} value={item.name}>{item.name}</option>)}
      </select>
    </label>
  ) : null;

  if (!data.length) {
    return <div className="flex h-52 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500">No matching data.</div>;
  }
  if (result?.visual?.chartType === 'pie') {
    const ringWidth = Math.max(8, Math.floor(84 / series.length));
    return <>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          {series.map((key, seriesIndex) => (
            <Pie
              key={key}
              data={data}
              dataKey={key}
              nameKey="name"
              innerRadius={series.length === 1 ? 0 : 16 + (seriesIndex * ringWidth)}
              outerRadius={series.length === 1 ? 92 : 16 + ((seriesIndex + 1) * ringWidth) - 2}
              onClick={(entry) => selectValue(entry?.name)}
            >
              {data.map((item, index) => <Cell key={`${key}-${item.name}-${index}`} fill={palette[index % palette.length]} opacity={!selectedValue || selectedValue === `${item.name}` ? 1 : 0.28} cursor={onSelect ? 'pointer' : 'default'} />)}
            </Pie>
          ))}
          <Tooltip /><Legend />
        </PieChart>
      </ResponsiveContainer>
      {selectionControl}
    </>;
  }
  const common = { data, margin: { top: 8, right: 16, bottom: 8, left: 0 } };
  if (result?.visual?.chartType === 'line') {
    return <>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart {...common} onClick={(state) => state?.activeLabel !== undefined && selectValue(state.activeLabel)} style={{ cursor: onSelect ? 'pointer' : 'default' }}>
          <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Legend />
          {series.map((key, index) => <Line key={key} type="monotone" dataKey={key} stroke={palette[index % palette.length]} strokeWidth={2} />)}
        </LineChart>
      </ResponsiveContainer>
      {selectionControl}
    </>;
  }
  return <>
    <ResponsiveContainer width="100%" height={280}>
      <BarChart {...common}>
        <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Legend />
        {series.map((key, index) => <Bar key={key} dataKey={key} fill={palette[index % palette.length]} radius={[4, 4, 0, 0]} onClick={(entry) => selectValue(entry?.name)}>{data.map((item) => <Cell key={`${key}_${item.name}`} fill={palette[index % palette.length]} opacity={!selectedValue || selectedValue === `${item.name}` ? 1 : 0.28} cursor={onSelect ? 'pointer' : 'default'} />)}</Bar>)}
      </BarChart>
    </ResponsiveContainer>
    {selectionControl}
  </>;
};

export default SheetChart;
